import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteIbkrTrade,
  getIbkrImportLedger,
  getIbkrImportRefreshStatus,
  getIbkrImports,
  getIbkrImportsRefreshStatus,
  getIbkrTradeDetail,
  rollbackIbkrImports,
  rollbackIbkrImportVersion,
} from '../api/ibkrOperations';
import useAuthenticatedRequest from './useAuthenticatedRequest';
import { broadcastImportComplete } from './useImportCompleteListener';

export const ACTIVE_IMPORT_STATES = ['queued', 'running', 'processing'];

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const EMPTY_COUNTS = {
  staged: null,
  processed: null,
  normalized: null,
  orders: null,
  executions: null,
  lotClosures: null,
  journalEntries: null,
  journalAllocations: null,
  journalNotes: null,
  recentExecutionsReset: null,
  errors: null,
  deleted: null,
};

const DEFAULT_REFRESH_STATUS = {
  status: null,
  message: null,
  error_code: null,
  task_id: null,
  import_version: null,
  started_at: null,
  finished_at: null,
  checked_at: null,
  counts: EMPTY_COUNTS,
  raw: null,
};

const pickFirst = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const arrayFromPayload = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.imports)) return payload.imports;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const countFrom = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['true', '1', 'yes', 'y'].includes(value.toLowerCase());
  return false;
};

export const normalizeImportStatus = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase().replace(/[-\s]+/g, '_');
  const aliases = {
    complete: 'succeeded',
    completed: 'succeeded',
    success: 'succeeded',
    ok: 'succeeded',
    errored: 'failed',
    error: 'failed',
    failure: 'failed',
    in_progress: 'running',
    pending: 'queued',
  };
  return aliases[normalized] || normalized;
};

const normalizeSide = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().toUpperCase();
  if (['BUY', 'B', 'BOT', 'LONG'].includes(normalized)) return 'BUY';
  if (['SELL', 'S', 'SLD', 'SHORT'].includes(normalized)) return 'SELL';
  return normalized;
};

const normalizeCounts = (data = {}) => {
  const counts = data?.counts && typeof data.counts === 'object' ? data.counts : {};
  const summary = data?.summary && typeof data.summary === 'object' ? data.summary : {};
  const impact = data?.impact_summary && typeof data.impact_summary === 'object' ? data.impact_summary : {};
  return {
    staged: countFrom(
      data.rows_staged,
      data.staged_rows,
      data.staging_rows,
      data.row_count,
      data.total_rows,
      data.total,
      data.staging_total,
      counts.staged,
      counts.rows_staged,
      summary.staged,
      impact.staged
    ),
    processed: countFrom(
      data.rows_processed,
      data.processed_rows,
      data.processed_count,
      data.staging_processed,
      data.canonical_orders,
      counts.processed,
      counts.rows_processed,
      counts.canonical_orders,
      counts.orders,
      summary.processed,
      summary.orders,
      impact.processed
    ),
    normalized: countFrom(
      data.normalized_rows,
      data.rows_normalized,
      data.normalized_count,
      data.trade_count,
      data.trades_count,
      data.execution_count,
      data.executions_count,
      data.canonical_executions,
      counts.normalized,
      counts.trades,
      counts.executions,
      counts.canonical_executions,
      summary.normalized,
      summary.trades,
      summary.executions,
      impact.normalized
    ),
    orders: countFrom(data.orders, data.canonical_orders, counts.orders, counts.canonical_orders, summary.orders, impact.orders),
    executions: countFrom(
      data.executions,
      data.canonical_executions,
      counts.executions,
      counts.canonical_executions,
      summary.executions,
      impact.executions
    ),
    lotClosures: countFrom(data.lot_closures, counts.lot_closures, summary.lot_closures, impact.lot_closures),
    journalEntries: countFrom(
      data.journal_entries,
      data.journal_entries_created,
      data.entries_count,
      data.journal_count,
      data.created_entries,
      counts.journal_entries,
      counts.entries,
      summary.journal_entries,
      summary.entries,
      summary.created,
      impact.journal_entries
    ),
    journalAllocations: countFrom(
      data.journal_allocations,
      counts.journal_allocations,
      summary.journal_allocations,
      impact.journal_allocations
    ),
    journalNotes: countFrom(data.journal_notes, counts.journal_notes, summary.journal_notes, impact.journal_notes),
    recentExecutionsReset: countFrom(
      data.recent_executions_reset,
      counts.recent_executions_reset,
      summary.recent_executions_reset,
      impact.recent_executions_reset
    ),
    errors: countFrom(
      data.error_count,
      data.errors_count,
      data.failed_rows,
      data.invalid_rows,
      counts.errors,
      counts.failed,
      summary.errors,
      summary.failed,
      impact.errors
    ),
    deleted: countFrom(
      data.deleted,
      data.deleted_count,
      data.rows_deleted,
      data.entries_deleted,
      counts.deleted,
      summary.deleted,
      impact.deleted
    ),
  };
};

const normalizeImpactSummary = (data = {}) => {
  const impact =
    pickFirst(data.impact_summary, data.rollback_impact, data.delete_impact, data.impact, data.summary) || null;
  if (!impact || typeof impact !== 'object') return null;
  return {
    ...impact,
    counts: normalizeCounts(impact),
  };
};

const importSortValue = (item) => {
  const versionNumber = toNumber(item.import_version);
  if (versionNumber !== null) return versionNumber;
  const time = Date.parse(item.finished_at || item.started_at || item.created_at || item.updated_at || '');
  return Number.isNaN(time) ? 0 : time;
};

const normalizeImport = (data, index = 0) => {
  const importVersion = pickFirst(data?.import_version, data?.importVersion, data?.version, data?.id);
  const status = normalizeImportStatus(
    pickFirst(data?.status, data?.sync_status, data?.state, data?.processing_status, data?.refresh_status)
  );
  const counts = normalizeCounts(data);
  const hasErrors = toBoolean(data?.has_errors) || toBoolean(data?.has_error) || (counts.errors ?? 0) > 0 || status === 'failed';

  return {
    id: String(importVersion ?? data?.id ?? index),
    import_version: importVersion,
    broker: pickFirst(data?.broker, data?.broker_name, data?.source, 'IBKR'),
    status,
    message: pickFirst(data?.message, data?.detail, data?.last_sync_message, data?.status_message),
    error_code: pickFirst(data?.error_code, data?.last_sync_error_code, data?.code),
    error_message: pickFirst(data?.error_message, data?.last_error, data?.error, data?.failure_reason),
    task_id: pickFirst(data?.task_id, data?.last_task_id, data?.celery_task_id),
    started_at: pickFirst(data?.started_at, data?.last_sync_started_at, data?.created_at, data?.created),
    finished_at: pickFirst(data?.finished_at, data?.last_sync_finished_at, data?.updated_at),
    created_at: pickFirst(data?.created_at, data?.created),
    account_id: pickFirst(data?.account_id, data?.client_account_id, data?.account),
    counts,
    has_errors: hasErrors,
    impact_summary: normalizeImpactSummary(data),
    raw: data || {},
  };
};

const normalizeRefreshStatus = (payload) => {
  if (!payload || typeof payload !== 'object') return DEFAULT_REFRESH_STATUS;
  return {
    status: normalizeImportStatus(
      pickFirst(payload.status, payload.sync_status, payload.state, payload.processing_status, payload.refresh_status)
    ),
    message: pickFirst(payload.message, payload.detail, payload.status_message, payload.last_sync_message),
    error_code: pickFirst(payload.error_code, payload.last_sync_error_code, payload.code),
    task_id: pickFirst(payload.task_id, payload.last_task_id, payload.celery_task_id),
    import_version: pickFirst(payload.import_version, payload.importVersion, payload.version),
    started_at: pickFirst(payload.started_at, payload.last_sync_started_at, payload.created_at),
    finished_at: pickFirst(payload.finished_at, payload.last_sync_finished_at, payload.updated_at),
    checked_at: pickFirst(payload.checked_at, payload.as_of, payload.updated_at),
    counts: normalizeCounts(payload),
    raw: payload,
  };
};

const normalizeLedgerRow = (data, index = 0, fallbackImportVersion = null) => {
  const tradeId = pickFirst(
    data?.id,
    data?.broker_trade_id,
    data?.trade?.id,
    data?.staging_trade_id,
    data?.normalized_trade_id,
    data?.canonical_trade_id,
    data?.tradeId
  );
  const journalId = pickFirst(
    data?.journal_entry_id,
    data?.journal_id,
    data?.entry_id,
    data?.journal?.id,
    data?.journal_entry?.id
  );
  const status = normalizeImportStatus(
    pickFirst(data?.status, data?.row_status, data?.processing_status, data?.normalization_status, data?.state)
  );
  const errorMessage = pickFirst(data?.error_message, data?.error, data?.last_error, data?.failure_reason);
  const hasError = toBoolean(data?.has_error) || toBoolean(data?.has_errors) || Boolean(errorMessage) || status === 'failed';

  return {
    id: String(pickFirst(data?.ledger_id, data?.row_id, data?.id, `${fallbackImportVersion || 'ledger'}-${index}`)),
    trade_id: tradeId,
    broker_trade_id: pickFirst(data?.trade_id, data?.source_transaction_id),
    execution_id: pickFirst(data?.execution_id, data?.exec_id, data?.ib_exec_id, data?.execution?.id, data?.trade_id),
    order_id: pickFirst(data?.order_id, data?.orderId, data?.broker_order_id, data?.brokerage_order_id, data?.order?.id),
    journal_entry_id: journalId,
    import_version: pickFirst(data?.import_version, data?.importVersion, data?.version, fallbackImportVersion),
    symbol: pickFirst(data?.symbol, data?.ticker, data?.underlying_symbol, data?.contract_symbol),
    side: normalizeSide(pickFirst(data?.side, data?.action, data?.buy_sell, data?.transaction_type, data?.position_type)),
    row_type: pickFirst(data?.row_type, data?.type, data?.record_type, data?.source_type),
    timestamp: pickFirst(
      data?.ts_utc,
      data?.timestamp,
      data?.trade_datetime,
      data?.trade_date,
      data?.executed_at,
      data?.execution_time,
      data?.date
    ),
    quantity: countFrom(data?.quantity, data?.qty, data?.qty_signed, data?.shares, data?.size),
    price: countFrom(data?.price, data?.trade_price, data?.execution_price, data?.avg_price),
    gross_money: countFrom(data?.gross_money, data?.gross_amount, data?.notional, data?.proceeds),
    commission: countFrom(data?.commission, data?.commissions, data?.fees, data?.fee),
    realized_pnl: countFrom(data?.realized_pnl, data?.pnl, data?.net_pnl, data?.profit_loss),
    status: status || (hasError ? 'failed' : null),
    has_error: hasError,
    error_message: errorMessage,
    provenance: pickFirst(data?.provenance, data?.normalization_provenance, data?.source, data?.normalization),
    raw: data || {},
  };
};

const normalizeTradeDetail = (payload, fallbackTradeId = null) => {
  const trade =
    payload?.broker_trade && typeof payload.broker_trade === 'object'
      ? payload.broker_trade
      : payload?.trade && typeof payload.trade === 'object'
        ? payload.trade
        : payload || {};
  const normalizedPayload = payload?.normalized && typeof payload.normalized === 'object' ? payload.normalized : {};
  const executions = arrayFromPayload(
    payload?.executions || payload?.execution_data || normalizedPayload.executions || trade.executions,
    [
    'executions',
    'rows',
    'items',
    ]
  );
  const ledgerRows = arrayFromPayload(payload?.ledger || payload?.ledger_rows || payload?.rows, ['ledger', 'ledger_rows', 'rows']);
  const journal =
    pickFirst(payload?.journal, payload?.journal_entry, payload?.journal_linkage, trade.journal, trade.journal_entry) || null;
  const fees = pickFirst(payload?.fees, payload?.fee_summary, payload?.commission_summary, trade.fees) || null;
  const pnl = pickFirst(payload?.pnl, payload?.pnl_summary, payload?.profit_loss, trade.pnl) || null;
  const provenance =
    pickFirst(
      payload?.normalization_provenance,
      payload?.provenance,
      payload?.normalization,
      trade.normalization_provenance,
      trade.provenance
    ) || null;

  return {
    trade_id: pickFirst(trade.id, trade.trade_id, fallbackTradeId),
    broker_trade_id: pickFirst(trade.trade_id, trade.source_transaction_id),
    symbol: pickFirst(trade.symbol, trade.ticker, payload?.symbol),
    side: normalizeSide(pickFirst(trade.side, trade.action, trade.position_type, payload?.side)),
    status: normalizeImportStatus(pickFirst(trade.status, trade.state, payload?.status)),
    import_version: pickFirst(trade.import_version, payload?.import_version, payload?.version),
    quantity: countFrom(trade.quantity, trade.qty, trade.shares, payload?.quantity),
    entry_price: countFrom(trade.entry_price, trade.entry, trade.avg_entry_price, payload?.entry_price),
    exit_price: countFrom(trade.exit_price, trade.exit, trade.avg_exit_price, payload?.exit_price),
    opened_at: pickFirst(trade.opened_at, trade.entry_datetime, trade.entry_time, payload?.opened_at),
    closed_at: pickFirst(trade.closed_at, trade.exit_datetime, trade.exit_time, payload?.closed_at),
    executions,
    orders: arrayFromPayload(normalizedPayload.orders, ['orders']),
    lot_closures: arrayFromPayload(normalizedPayload.lot_closures, ['lot_closures']),
    ledger_rows: ledgerRows,
    journal,
    fees,
    pnl,
    provenance,
    impact_summary: normalizeImpactSummary(payload),
    raw: payload || {},
  };
};

export const extractIbkrOperationsErrorMessage = (err, fallback = 'The IBKR operation failed. Please try again.') => {
  if (!err) return fallback;
  const data = err.response?.data;
  const msg = data?.message || data?.detail || data?.error;
  if (msg) return String(msg);
  if (err.message) return err.message;
  return fallback;
};

const confirmationTokenFrom = (payload) =>
  pickFirst(
    payload?.confirmation_token,
    payload?.impact?.confirmation_token,
    payload?.rollback_impact?.confirmation_token,
    payload?.delete_impact?.confirmation_token,
    payload?.summary?.confirmation_token
  );

const confirmedPayload = (dryRunPayload, extra = {}) => {
  const token = confirmationTokenFrom(dryRunPayload);
  if (!token) {
    throw new Error('The backend did not return a confirmation token for this operation.');
  }
  return {
    confirm: true,
    confirmation_token: token,
    ...extra,
  };
};

export default function useIbkrImportOperations({ enabled = true, preferredImportVersion = null } = {}) {
  const { isLoaded, isSignedIn } = useAuthenticatedRequest();
  const [imports, setImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [importsLoaded, setImportsLoaded] = useState(false);
  const [importsError, setImportsError] = useState(null);
  const [allRefreshStatus, setAllRefreshStatus] = useState(DEFAULT_REFRESH_STATUS);
  const [selectedImportVersion, setSelectedImportVersion] = useState(preferredImportVersion || '');
  const [selectedRefreshStatus, setSelectedRefreshStatus] = useState(DEFAULT_REFRESH_STATUS);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);
  const [tradeDetail, setTradeDetail] = useState(null);
  const [tradeDetailLoading, setTradeDetailLoading] = useState(false);
  const [tradeDetailError, setTradeDetailError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const mountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);

  const canLoad = enabled && isLoaded && isSignedIn;

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (preferredImportVersion && !selectedImportVersion) {
      setSelectedImportVersion(preferredImportVersion);
    }
  }, [preferredImportVersion, selectedImportVersion]);

  const loadImports = useCallback(async () => {
    if (!canLoad) {
      setImports([]);
      if (isLoaded) setImportsLoaded(true);
      return [];
    }
    setImportsLoading(true);
    setImportsError(null);
    try {
      const payload = await getIbkrImports();
      const normalized = arrayFromPayload(payload)
        .map(normalizeImport)
        .sort((a, b) => importSortValue(b) - importSortValue(a));
      if (!mountedRef.current) return normalized;
      setImports(normalized);
      setSelectedImportVersion((current) => {
        if (current && normalized.some((item) => String(item.import_version) === String(current))) return current;
        if (preferredImportVersion && normalized.some((item) => String(item.import_version) === String(preferredImportVersion))) {
          return preferredImportVersion;
        }
        return normalized[0]?.import_version || current || '';
      });
      return normalized;
    } catch (err) {
      if (mountedRef.current) setImportsError(err);
      return [];
    } finally {
      if (mountedRef.current) {
        setImportsLoading(false);
        setImportsLoaded(true);
      }
    }
  }, [canLoad, preferredImportVersion]);

  const loadAllRefreshStatus = useCallback(async () => {
    if (!canLoad) {
      setAllRefreshStatus(DEFAULT_REFRESH_STATUS);
      return DEFAULT_REFRESH_STATUS;
    }
    try {
      const payload = await getIbkrImportsRefreshStatus();
      const normalized = normalizeRefreshStatus(payload);
      if (mountedRef.current) setAllRefreshStatus(normalized);
      return normalized;
    } catch (err) {
      if (mountedRef.current) {
        setAllRefreshStatus((prev) => ({
          ...prev,
          status: 'failed',
          message: extractIbkrOperationsErrorMessage(err, 'Could not load import refresh status.'),
        }));
      }
      return DEFAULT_REFRESH_STATUS;
    }
  }, [canLoad]);

  const loadSelectedRefreshStatus = useCallback(
    async (importVersion = selectedImportVersion) => {
      if (!canLoad || !importVersion) {
        setSelectedRefreshStatus(DEFAULT_REFRESH_STATUS);
        return DEFAULT_REFRESH_STATUS;
      }
      try {
        const payload = await getIbkrImportRefreshStatus(importVersion);
        const normalized = normalizeRefreshStatus(payload);
        if (mountedRef.current) setSelectedRefreshStatus(normalized);
        return normalized;
      } catch (err) {
        if (mountedRef.current) {
          setSelectedRefreshStatus((prev) => ({
            ...prev,
            status: 'failed',
            message: extractIbkrOperationsErrorMessage(err, 'Could not load selected import status.'),
          }));
        }
        return DEFAULT_REFRESH_STATUS;
      }
    },
    [canLoad, selectedImportVersion]
  );

  const loadLedger = useCallback(
    async (importVersion = selectedImportVersion) => {
      if (!canLoad || !importVersion) {
        setLedgerRows([]);
        return [];
      }
      setLedgerLoading(true);
      setLedgerError(null);
      try {
        const payload = await getIbkrImportLedger(importVersion);
        const normalized = arrayFromPayload(payload, ['ledger', 'ledger_rows', 'rows', 'results'])
          .map((row, index) => normalizeLedgerRow(row, index, importVersion));
        if (!mountedRef.current) return normalized;
        setLedgerRows(normalized);
        return normalized;
      } catch (err) {
        if (mountedRef.current) setLedgerError(err);
        return [];
      } finally {
        if (mountedRef.current) setLedgerLoading(false);
      }
    },
    [canLoad, selectedImportVersion]
  );

  const reload = useCallback(async () => {
    const [loadedImports] = await Promise.all([loadImports(), loadAllRefreshStatus()]);
    const version =
      selectedImportVersion ||
      preferredImportVersion ||
      loadedImports.find((item) => item.import_version)?.import_version ||
      '';
    if (version) {
      await Promise.all([loadSelectedRefreshStatus(version), loadLedger(version)]);
    }
  }, [
    loadAllRefreshStatus,
    loadImports,
    loadLedger,
    loadSelectedRefreshStatus,
    preferredImportVersion,
    selectedImportVersion,
  ]);

  const loadTradeDetail = useCallback(
    async (tradeId) => {
      if (!canLoad || !tradeId) {
        setTradeDetail(null);
        return null;
      }
      setTradeDetailLoading(true);
      setTradeDetailError(null);
      try {
        const payload = await getIbkrTradeDetail(tradeId);
        const normalized = normalizeTradeDetail(payload, tradeId);
        if (mountedRef.current) setTradeDetail(normalized);
        return normalized;
      } catch (err) {
        if (mountedRef.current) setTradeDetailError(err);
        return null;
      } finally {
        if (mountedRef.current) setTradeDetailLoading(false);
      }
    },
    [canLoad]
  );

  const previewRollbackImport = useCallback(
    async (importVersion = selectedImportVersion) => {
      if (!canLoad || !importVersion) return { ok: false, error: new Error('No import version selected.') };
      setPendingAction('preview-rollback-import');
      try {
        const dryRunPayload = await rollbackIbkrImportVersion(importVersion, { dry_run: true });
        return { ok: true, data: dryRunPayload, impact: normalizeImpactSummary(dryRunPayload) };
      } catch (err) {
        return { ok: false, error: err };
      } finally {
        if (mountedRef.current) setPendingAction(null);
      }
    },
    [canLoad, selectedImportVersion]
  );

  const confirmRollbackImport = useCallback(
    async (importVersion = selectedImportVersion, dryRunPayload) => {
      if (!canLoad || !importVersion) return { ok: false, error: new Error('No import version selected.') };
      setPendingAction('rollback-import');
      try {
        const payload = await rollbackIbkrImportVersion(importVersion, confirmedPayload(dryRunPayload));
        await reload();
        const refreshStatus = await loadSelectedRefreshStatus(importVersion);
        broadcastImportComplete({ broker: 'IBKR', action: 'rollback', import_version: importVersion });
        return { ok: true, data: payload, refreshStatus };
      } catch (err) {
        return { ok: false, error: err };
      } finally {
        if (mountedRef.current) setPendingAction(null);
      }
    },
    [canLoad, loadSelectedRefreshStatus, reload, selectedImportVersion]
  );

  const rollbackSelectedImport = useCallback(
    async (importVersion = selectedImportVersion) => {
      const dryRun = await previewRollbackImport(importVersion);
      if (!dryRun.ok) return dryRun;
      return confirmRollbackImport(importVersion, dryRun.data);
    },
    [confirmRollbackImport, previewRollbackImport, selectedImportVersion]
  );

  const rollbackLatestImport = useCallback(async () => {
    if (!canLoad) return { ok: false, error: new Error('Authentication is not ready.') };
    setPendingAction('rollback-latest');
    try {
      const dryRunPayload = await rollbackIbkrImports({ dry_run: true });
      const payload = await rollbackIbkrImports(confirmedPayload(dryRunPayload));
      await reload();
      broadcastImportComplete({ broker: 'IBKR', action: 'rollback_latest' });
      return { ok: true, data: payload };
    } catch (err) {
      return { ok: false, error: err };
    } finally {
      if (mountedRef.current) setPendingAction(null);
    }
  }, [canLoad, reload]);

  const previewDeleteTrade = useCallback(
    async (tradeId) => {
      if (!canLoad || !tradeId) return { ok: false, error: new Error('No trade selected.') };
      setPendingAction('preview-delete-trade');
      try {
        const dryRunPayload = await deleteIbkrTrade(tradeId, { dry_run: true });
        return { ok: true, data: dryRunPayload, impact: normalizeImpactSummary(dryRunPayload) };
      } catch (err) {
        return { ok: false, error: err };
      } finally {
        if (mountedRef.current) setPendingAction(null);
      }
    },
    [canLoad]
  );

  const confirmDeleteTrade = useCallback(
    async (tradeId, dryRunPayload, importVersion = selectedImportVersion) => {
      if (!canLoad || !tradeId) return { ok: false, error: new Error('No trade selected.') };
      setPendingAction('delete-trade');
      try {
        const payload = await deleteIbkrTrade(tradeId, confirmedPayload(dryRunPayload));
        const refreshVersion = importVersion || selectedImportVersion;
        const [, , refreshStatus] = await Promise.all([
          loadLedger(refreshVersion),
          loadImports(),
          refreshVersion ? loadSelectedRefreshStatus(refreshVersion) : loadSelectedRefreshStatus(),
        ]);
        setTradeDetail(null);
        broadcastImportComplete({ broker: 'IBKR', action: 'delete_trade', trade_id: tradeId });
        return { ok: true, data: payload, refreshStatus };
      } catch (err) {
        return { ok: false, error: err };
      } finally {
        if (mountedRef.current) setPendingAction(null);
      }
    },
    [canLoad, loadImports, loadLedger, loadSelectedRefreshStatus, selectedImportVersion]
  );

  const removeTrade = useCallback(
    async (tradeId) => {
      const dryRun = await previewDeleteTrade(tradeId);
      if (!dryRun.ok) return dryRun;
      return confirmDeleteTrade(tradeId, dryRun.data);
    },
    [confirmDeleteTrade, previewDeleteTrade]
  );

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, reload]);

  useEffect(() => {
    if (!canLoad || !selectedImportVersion) {
      setLedgerRows([]);
      setSelectedRefreshStatus(DEFAULT_REFRESH_STATUS);
      return;
    }
    loadSelectedRefreshStatus(selectedImportVersion);
    loadLedger(selectedImportVersion);
  }, [canLoad, loadLedger, loadSelectedRefreshStatus, selectedImportVersion]);

  const latestImport = imports[0] || null;
  const selectedImport = useMemo(
    () => imports.find((item) => String(item.import_version) === String(selectedImportVersion)) || null,
    [imports, selectedImportVersion]
  );

  const activeStatus = normalizeImportStatus(
    selectedRefreshStatus.status || selectedImport?.status || allRefreshStatus.status || latestImport?.status
  );

  useEffect(() => {
    if (!canLoad || !ACTIVE_IMPORT_STATES.includes(activeStatus)) {
      pollDeadlineRef.current = null;
      return undefined;
    }
    if (!pollDeadlineRef.current) {
      pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    }
    if (Date.now() > pollDeadlineRef.current) {
      return undefined;
    }

    pollTimerRef.current = setTimeout(() => {
      loadImports();
      loadAllRefreshStatus();
      if (selectedImportVersion) {
        loadSelectedRefreshStatus(selectedImportVersion);
        loadLedger(selectedImportVersion);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [
    activeStatus,
    canLoad,
    loadAllRefreshStatus,
    loadImports,
    loadLedger,
    loadSelectedRefreshStatus,
    selectedImportVersion,
  ]);

  return {
    imports,
    latestImport,
    selectedImport,
    selectedImportVersion,
    setSelectedImportVersion,
    allRefreshStatus,
    selectedRefreshStatus,
    ledgerRows,
    tradeDetail,
    loading: importsLoading || ledgerLoading,
    importsLoading,
    importsLoaded,
    ledgerLoading,
    tradeDetailLoading,
    errors: {
      imports: importsError,
      ledger: ledgerError,
      tradeDetail: tradeDetailError,
    },
    pendingAction,
    reload,
    loadImports,
    loadLedger,
    loadTradeDetail,
    clearTradeDetail: () => {
      setTradeDetail(null);
      setTradeDetailError(null);
    },
    previewRollbackImport,
    confirmRollbackImport,
    rollbackSelectedImport,
    rollbackLatestImport,
    previewDeleteTrade,
    confirmDeleteTrade,
    removeTrade,
  };
}
